import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'react-bootstrap-icons';
import './CustomDropdown.css';

export interface CustomDropdownOption
{
    value: string;
    label: string;
}

interface CustomDropdownProps
{
    value: string;
    options: Array<string | CustomDropdownOption>;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    invalid?: boolean;
    className?: string;
}

function normalizeOption(option: string | CustomDropdownOption): CustomDropdownOption
{
    if (typeof option === 'string')
    {
        return { value: option, label: option };
    }
    return option;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
    value,
    options,
    onChange,
    placeholder = 'Select option',
    disabled = false,
    invalid = false,
    className = '',
}) =>
{
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const normalizedOptions = useMemo(() => options.map(normalizeOption), [options]);

    const displayValue = useMemo(() =>
    {
        if (value && value.trim())
        {
            return normalizedOptions.find((option) => option.value === value)?.label || value;
        }
        return placeholder;
    }, [normalizedOptions, placeholder, value]);

    useEffect(() =>
    {
        function handleDocumentClick(event: MouseEvent): void
        {
            if (!rootRef.current)
            {
                return;
            }

            const targetNode = event.target as Node;
            if (!rootRef.current.contains(targetNode) && !menuRef.current?.contains(targetNode))
            {
                setOpen(false);
            }
        }

        document.addEventListener('mousedown', handleDocumentClick);
        return () => document.removeEventListener('mousedown', handleDocumentClick);
    }, []);

    useEffect(() =>
    {
        if (!open)
        {
            setMenuStyle(null);
            return undefined;
        }

        function updateMenuPosition(): void
        {
            if (!rootRef.current)
            {
                return;
            }

            const rect = rootRef.current.getBoundingClientRect();
            const viewportPadding = 12;
            const width = Math.min(rect.width, Math.max(0, window.innerWidth - (viewportPadding * 2)));
            const left = Math.min(
                Math.max(rect.left, viewportPadding),
                Math.max(viewportPadding, window.innerWidth - width - viewportPadding),
            );
            const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
            const openAbove = spaceBelow < 160 && rect.top > spaceBelow;
            setMenuStyle({
                position: 'fixed',
                top: openAbove ? undefined : rect.bottom + 6,
                bottom: openAbove ? window.innerHeight - rect.top + 6 : undefined,
                left,
                width,
                maxHeight: Math.min(240, Math.max(120, openAbove ? rect.top - 18 : spaceBelow)),
            });
        }

        updateMenuPosition();
        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);

        return () =>
        {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [open]);

    function handleToggle(): void
    {
        if (!disabled)
        {
            setOpen((prev) => !prev);
        }
    }

    function handleSelect(selected: string): void
    {
        onChange(selected);
        setOpen(false);
    }

    const menu = open && menuStyle ? createPortal(
        <div ref={menuRef} className="cd-menu cd-menu-portal" style={menuStyle}>
            {normalizedOptions.map((opt) => (
                <button
                    key={opt.value}
                    type="button"
                    className={`cd-option ${value === opt.value ? 'selected' : ''}`}
                    onClick={() => handleSelect(opt.value)}
                >
                    {opt.label}
                </button>
            ))}
        </div>,
        document.body,
    ) : null;

    return (
        <>
        <div className={`cd-root ${className}`.trim()} ref={rootRef} data-dropdown-open={open ? 'true' : 'false'}>
            <button
                type="button"
                className={`cd-btn ${open ? 'cd-open' : ''} ${invalid ? 'cd-invalid' : ''}`}
                onClick={handleToggle}
                disabled={disabled}
                aria-expanded={open}
            >
                <span className={`cd-value ${value ? 'cd-has-value' : 'cd-placeholder'}`}>{displayValue}</span>
                <ChevronDown className="cd-chevron" />
            </button>
        </div>
        {menu}
        </>
    );
};

export default CustomDropdown;
